import React, { useMemo } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { Send } from "react-bootstrap-icons";
import ApexCharts from "react-apexcharts";

// Canonical status colors from PreauthorizationVolume.js
const statusConfig = {
  underProcessing: {
    color: "#a9e2fd",
    bg: "#fff",
    label: "Under Processing",
  },
  paid: { color: "#00965e", bg: "#e7faf1", label: "Approved" },
  partiallyPaid: {
    color: "#0da9bd",
    bg: "#e6f9fc",
    label: "Partially Approved",
  },
  rejected: { color: "#ed5e0b", bg: "#fff2e9", label: "Rejected" },
};

const AFTER_METRICS = [
  {
    title: "Number of Claims",
    totalDisplay: "17.1k Claims",
    underProcessingVal: "865 Claims",
    underProcessingPct: 5.05,
    paidVal: "12.4k Claims",
    paidPct: 72.69,
    partiallyPaidVal: "1.8k Claims",
    partiallyPaidPct: 10.27,
    rejectedVal: "1.4k Claims",
    rejectedPct: 8.29,
  },
  {
    title: "Gross Amount",
    totalDisplay: "4371.2k SAR",
    underProcessingVal: "256.3k SAR",
    underProcessingPct: 5.86,
    paidVal: "3180.2k SAR",
    paidPct: 72.75,
    partiallyPaidVal: "424.7k SAR",
    partiallyPaidPct: 9.72,
    rejectedVal: "510.0k SAR",
    rejectedPct: 11.67,
  },
  {
    title: "Discount",
    totalDisplay: "466.3k SAR",
    underProcessingVal: "16.3k SAR",
    underProcessingPct: 3.5,
    paidVal: "338.5k SAR",
    paidPct: 72.59,
    partiallyPaidVal: "51.5k SAR",
    partiallyPaidPct: 11.04,
    rejectedVal: "60.0k SAR",
    rejectedPct: 12.87,
  },
  {
    title: "Patient Share",
    totalDisplay: "196.2k SAR",
    underProcessingVal: "16.9k SAR",
    underProcessingPct: 8.62,
    paidVal: "138.1k SAR",
    paidPct: 70.39,
    partiallyPaidVal: "18.2k SAR",
    partiallyPaidPct: 9.28,
    rejectedVal: "23.0k SAR",
    rejectedPct: 11.71,
  },
  {
    title: "Net Amount",
    totalDisplay: "3708.7k SAR",
    underProcessingVal: "223.1k SAR",
    underProcessingPct: 6.01,
    paidVal: "2703.6k SAR",
    paidPct: 72.9,
    partiallyPaidVal: "355.0k SAR",
    partiallyPaidPct: 9.57,
    rejectedVal: "427.0k SAR",
    rejectedPct: 11.52,
  },
  {
    title: "VAT Amount",
    totalDisplay: "466.4k SAR",
    underProcessingVal: "33.2k SAR",
    underProcessingPct: 7.11,
    paidVal: "338.2k SAR",
    paidPct: 72.52,
    partiallyPaidVal: "43.0k SAR",
    partiallyPaidPct: 9.22,
    rejectedVal: "52.0k SAR",
    rejectedPct: 11.15,
  },
];

const StatsAllClaimAfterSubmission = () => {
  const chartColors = useMemo(
    () => [
      statusConfig.underProcessing.color,
      statusConfig.paid.color,
      statusConfig.partiallyPaid.color,
      statusConfig.rejected.color,
    ],
    [],
  );

  const getChartOptions = (labels) => ({
    chart: {
      type: "donut",
      sparkline: { enabled: true },
      animations: { enabled: true },
    },
    colors: chartColors,
    labels: labels,
    tooltip: {
      enabled: true,
      followCursor: false,
      fixed: {
        enabled: true,
        position: "topLeft",
        offsetX: -100,
        offsetY: -8,
      },
      style: {
        fontSize: "11px",
      },
      y: {
        formatter: (val) => `${val.toFixed(2)}%`,
      },
    },
    stroke: {
      width: 0,
    },
    plotOptions: {
      pie: {
        donut: {
          size: "68%",
        },
      },
    },
  });

  return (
    <Col xs={12} className="mt-3">
      <Card className="h-100 shadow-sm border">
        {/* Header with Title & Legend */}
        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center p-3 justify-content-between pb-3 border-bottom gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                backgroundColor: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                border: "1px solid rgba(59, 130, 246, 0.25)",
              }}
            >
              <Send size={15} />
            </div>
            <div>
              <h6
                className="mb-0 fw-bold"
                style={{ fontSize: "14px" }}
              >
                All Claims After Submission
              </h6>
              <small className="text-muted" style={{ fontSize: "11px" }}>
                Post-submission NPHIES adjudication statuses and financial
                distribution
              </small>
            </div>
          </div>

          {/* Legend */}
          <div
            className="d-flex flex-wrap align-items-center gap-3 fw-semi-bold"
            style={{ fontSize: "12px" }}
          >
            <div
              className="d-flex align-items-center gap-1"
              style={{ color: statusConfig.underProcessing.color }}
            >
              <span
                className="rounded-circle d-inline-block"
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: statusConfig.underProcessing.color,
                }}
              ></span>
              <span>{statusConfig.underProcessing.label}</span>
            </div>
            <div
              className="d-flex align-items-center gap-1"
              style={{ color: statusConfig.paid.color }}
            >
              <span
                className="rounded-circle d-inline-block"
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: statusConfig.paid.color,
                }}
              ></span>
              <span>{statusConfig.paid.label}</span>
            </div>
            <div
              className="d-flex align-items-center gap-1"
              style={{ color: statusConfig.partiallyPaid.color }}
            >
              <span
                className="rounded-circle d-inline-block"
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: statusConfig.partiallyPaid.color,
                }}
              ></span>
              <span>{statusConfig.partiallyPaid.label}</span>
            </div>
            <div
              className="d-flex align-items-center gap-1"
              style={{ color: statusConfig.rejected.color }}
            >
              <span
                className="rounded-circle d-inline-block"
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: statusConfig.rejected.color,
                }}
              ></span>
              <span>{statusConfig.rejected.label}</span>
            </div>
          </div>
        </Card.Header>

        {/* 6 Grid Cards */}
        <Card.Body className="p-3">
          <Row className="g-3">
            {AFTER_METRICS.map((m) => {
              const series = [
                m.underProcessingPct,
                m.paidPct,
                m.partiallyPaidPct,
                m.rejectedPct,
              ];
              const labels = [
                statusConfig.underProcessing.label,
                statusConfig.paid.label,
                statusConfig.partiallyPaid.label,
                statusConfig.rejected.label,
              ];

              return (
                <Col xs={12} sm={6} md={4} lg={2} key={m.title}>
                  <div
                    className="p-3 rounded border h-100 d-flex flex-column justify-content-between"
                    style={{ transition: "all 0.2s ease" }}
                  >
                    <div>
                      <div className="d-flex align-items-start justify-content-between gap-1">
                        <div>
                          <div
                            className="fw-semi-bold text-muted text-uppercase"
                            style={{ fontSize: "11px" }}
                          >
                            {m.title}
                          </div>
                          <div
                            className="fw-bold font-monospace mt-1"
                            style={{ fontSize: "16px" }}
                          >
                            {m.totalDisplay}
                          </div>
                        </div>

                        {/* Donut Chart Ring */}
                        <div
                          className="flex-shrink-0"
                          style={{ width: "52px", height: "52px" }}
                        >
                          <ApexCharts
                            options={getChartOptions(labels)}
                            series={series}
                            type="donut"
                            width={52}
                            height={52}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Breakdown List */}
                    <div
                      className="mt-3 pt-2 border-top space-y-1"
                      style={{ fontSize: "10px" }}
                    >
                      {/* Under Processing */}
                      <div className="d-flex align-items-center justify-content-between fw-semi-bold mb-1">
                        <div className="d-flex align-items-center gap-1 text-muted">
                          <span
                            className="rounded-circle d-inline-block"
                            style={{
                              width: "6px",
                              height: "6px",
                              backgroundColor:
                                statusConfig.underProcessing.color,
                            }}
                          ></span>
                          <span
                            className="text-truncate"
                            style={{ maxWidth: "80px" }}
                          >
                            {m.underProcessingVal}
                          </span>
                        </div>
                        <span
                          className="font-monospace fw-bold"
                          style={{ color: statusConfig.underProcessing.color }}
                        >
                          {m.underProcessingPct.toFixed(1)}%
                        </span>
                      </div>

                      {/* Paid */}
                      <div className="d-flex align-items-center justify-content-between fw-semi-bold mb-1">
                        <div className="d-flex align-items-center gap-1 text-muted">
                          <span
                            className="rounded-circle d-inline-block"
                            style={{
                              width: "6px",
                              height: "6px",
                              backgroundColor: statusConfig.paid.color,
                            }}
                          ></span>
                          <span
                            className="text-truncate"
                            style={{ maxWidth: "80px" }}
                          >
                            {m.paidVal}
                          </span>
                        </div>
                        <span
                          className="font-monospace fw-bold"
                          style={{ color: statusConfig.paid.color }}
                        >
                          {m.paidPct.toFixed(1)}%
                        </span>
                      </div>

                      {/* Partially Paid */}
                      <div className="d-flex align-items-center justify-content-between fw-semi-bold mb-1">
                        <div className="d-flex align-items-center gap-1 text-muted">
                          <span
                            className="rounded-circle d-inline-block"
                            style={{
                              width: "6px",
                              height: "6px",
                              backgroundColor: statusConfig.partiallyPaid.color,
                            }}
                          ></span>
                          <span
                            className="text-truncate"
                            style={{ maxWidth: "80px" }}
                          >
                            {m.partiallyPaidVal}
                          </span>
                        </div>
                        <span
                          className="font-monospace fw-bold"
                          style={{ color: statusConfig.partiallyPaid.color }}
                        >
                          {m.partiallyPaidPct.toFixed(1)}%
                        </span>
                      </div>

                      {/* Rejected */}
                      <div className="d-flex align-items-center justify-content-between fw-semi-bold">
                        <div className="d-flex align-items-center gap-1 text-muted">
                          <span
                            className="rounded-circle d-inline-block"
                            style={{
                              width: "6px",
                              height: "6px",
                              backgroundColor: statusConfig.rejected.color,
                            }}
                          ></span>
                          <span
                            className="text-truncate"
                            style={{ maxWidth: "80px" }}
                          >
                            {m.rejectedVal}
                          </span>
                        </div>
                        <span
                          className="font-monospace fw-bold"
                          style={{ color: statusConfig.rejected.color }}
                        >
                          {m.rejectedPct.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default StatsAllClaimAfterSubmission;
